/**
 * Keeps the "with N items" tier badge in step with the price the card is
 * actually showing.
 *
 * WHY THIS IS NEEDED. product-prices.liquid renders a product card at the
 * product's LOWEST variant price. The theme then swaps the displayed price
 * client-side when a variant swatch is selected or defaulted. So a Sienna card
 * server-renders $78 (silver) and immediately becomes $108 (gold), while a
 * server-rendered badge stays at 60% of $78. The two then disagree on screen,
 * which is worse than having no badge at all: it quotes a price for a variant
 * the shopper is not looking at.
 *
 * WHY A MUTATION OBSERVER rather than listening for a variant event. The card
 * swatches are rendered and wired by theme code whose event names are not
 * stable across card layouts, and the price also changes on filtering and on
 * infinite-scroll reflow. Watching the price node itself catches every path
 * without needing to know which one fired.
 *
 * The badge derives from the DISPLAYED price rather than from variant data, so
 * it is correct by construction: whatever the card says, the badge is that
 * number times the tier multiplier.
 */
(function tierCardBadge() {
  if (window.__mTierCardBadge) return;
  window.__mTierCardBadge = true;

  const PRICE_SELECTOR = '.m-price-item--regular';

  /** Split "$1,234.50" into its symbol, its number, and its decimal places. */
  function parseMoney(text) {
    const raw = String(text || '').trim();
    const match = raw.match(/([0-9][0-9.,\s]*)/);
    if (!match) return null;

    const numeric = match[1].replace(/[\s,]/g, '');
    const value = parseFloat(numeric);
    if (!isFinite(value)) return null;

    const decimals = (numeric.split('.')[1] || '').length;
    return {
      value: value,
      decimals: decimals === 0 ? 0 : 2,
      prefix: raw.slice(0, match.index),
      suffix: raw.slice(match.index + match[1].length),
    };
  }

  function format(money, value) {
    const fixed = value.toFixed(money.decimals);
    const [whole, frac] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return money.prefix + grouped + (frac ? '.' + frac : '') + money.suffix;
  }

  function update(badge) {
    const card = badge.closest('.m-product-card');
    if (!card) return;

    const priceEl = card.querySelector(PRICE_SELECTOR);
    if (!priceEl) return;

    const money = parseMoney(priceEl.textContent);
    if (!money) return;

    const pct = Number(badge.dataset.tierPct);
    const items = badge.dataset.tierItems;
    if (!isFinite(pct) || pct <= 0 || pct >= 100) return;

    const target = format(money, money.value * (100 - pct) / 100);

    // Only touch the DOM when the number actually changed, so the observer
    // cannot trigger itself in a loop.
    const strong = badge.querySelector('strong');
    if (!strong || strong.textContent.trim() === target) return;

    strong.textContent = target;
    badge.lastChild.textContent = ' with ' + items + ' items';
  }

  function watch(badge) {
    if (badge.__tierWatched) return;
    badge.__tierWatched = true;

    const card = badge.closest('.m-product-card');
    const priceEl = card && card.querySelector(PRICE_SELECTOR);
    if (!priceEl) return;

    update(badge);
    new MutationObserver(() => update(badge)).observe(priceEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function scan() {
    document.querySelectorAll('.m-tier-badge[data-tier-pct]').forEach(watch);
  }

  scan();
  document.addEventListener('DOMContentLoaded', scan, { once: true });

  // Cards arrive later from filtering, pagination and quick-view.
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.addedNodes.length) {
        scan();
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
